import { Combobox } from '../../../Combobox';
import { Table } from '@tanstack/react-table';
import { FloatingLabelProps } from '../../../FloatingLabel';
import { useMemo } from 'react';
import { ColumnDefMeta } from '../../types';
import useI18n from '../../../../hooks/useI18n';

interface ColumnFilterComboboxProps<TData> {
  table: Table<TData>;
  columnId: string;
}

export function ColumnFilterCombobox<TData>({
  table,
  columnId,
}: ColumnFilterComboboxProps<TData>) {
  const i18n = useI18n();

  const column = table.getColumn(columnId);
  const definition = column?.columnDef;
  const meta = definition?.meta as ColumnDefMeta;

  const items = useMemo(() => {
    if (!column) {
      return [];
    }

    if (meta?.filterVariant?.includes('select')) {
      if (!meta?.filterOptions) {
        throw new Error(
          `ColumnFilterCombobox: No filterData provided for column ${columnId}`,
        );
      }

      return meta?.filterOptions?.map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return { label: item.toString(), value: item.toString() };
        }
        return item;
      });
    }

    throw new Error(
      `ColumnFilterCombobox: Unknown filter type ${meta?.filterVariant}`,
    );
  }, [column, columnId, meta?.filterOptions, meta?.filterVariant]);

  if (!column) {
    return null;
  }
  const multiselect = meta?.filterVariant === 'multiselect';

  const defaultLabelProps: FloatingLabelProps = {
    label: column.columnDef.header?.toString(),
    float: 'top',
  };

  if (multiselect) {
    return (
      <Combobox
        labelProps={defaultLabelProps}
        multiSelect
        fullWidth
        items={items}
        value={(column.getFilterValue() as string[] | undefined) ?? []}
        onSelect={(vals) => {
          return column.setFilterValue(vals.length > 0 ? vals : undefined);
        }}
        placeholder={i18n.t('value')}
        itemRenderer={meta?.filterRenderer}
        showClearButton
        shouldFilter={false}
      />
    );
  }

  return (
    <Combobox
      labelProps={defaultLabelProps}
      items={items}
      fullWidth
      value={(column.getFilterValue() as string | undefined) ?? ''}
      onSelect={(val) => column.setFilterValue(val)}
      placeholder={i18n.t('value')}
      itemRenderer={meta?.filterRenderer}
      showClearButton
      shouldFilter={false}
    />
  );
}
